import React, { useEffect, useState, useRef, ReactNode } from 'react';
import * as signalR from '@microsoft/signalr';
import { ConnectionState, ExecutionEvent } from '../types';
import { SignalRContext } from './signalRContextDef';
import { getAuthToken } from '../services/authTokenService';

interface SignalRProviderProps {
  children: ReactNode;
}

export const SignalRProvider: React.FC<SignalRProviderProps> = ({ children }) => {
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>({
    isConnected: false,
    isConnecting: false
  });
  const executionHandlersRef = useRef<Map<string, Record<string, (event: any) => void>>>(new Map());

  useEffect(() => {
    const hubUrl = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:5117'}/hubs/agent-execution`;
    
    const connectionBuilder = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        // Use Bearer token instead of cookies for authentication
        accessTokenFactory: () => getAuthToken() || '',
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Information);

    const newConnection = connectionBuilder.build();

    newConnection.onclose((error) => {
      setConnectionState({ 
        isConnected: false, 
        isConnecting: false,
        error: error?.message
      });
    });

    newConnection.onreconnecting((error) => {
      setConnectionState({ 
        isConnected: false, 
        isConnecting: true,
        error: error?.message
      });
    });

    newConnection.onreconnected(() => {
      setConnectionState({ 
        isConnected: true, 
        isConnecting: false,
        error: undefined
      });
    });

    const startConnection = async () => {
      try {
        setConnectionState({ isConnected: false, isConnecting: true });
        await newConnection.start();
        setConnectionState({ isConnected: true, isConnecting: false });
      } catch (error) {
        console.error('SignalR connection failed:', error);
        setConnectionState({ 
          isConnected: false, 
          isConnecting: false, 
          error: error instanceof Error ? error.message : 'Connection failed' 
        });
      }
    };

    connectionRef.current = newConnection;
    startConnection();

    return () => {
      newConnection.stop();
    };
  }, []);

  const ensureConnection = async (): Promise<boolean> => {
    const currentConnection = connectionRef.current;
    
    if (connectionState.isConnected && currentConnection?.state === signalR.HubConnectionState.Connected) {
      return true;
    }

    if (!currentConnection) {
      console.warn('No connection available for reconnection');
      return false;
    }

    if (currentConnection.state === signalR.HubConnectionState.Disconnected) {
      try {
        setConnectionState({ isConnected: false, isConnecting: true });
        await currentConnection.start();
        setConnectionState({ isConnected: true, isConnecting: false });
        return true;
      } catch (error) {
        console.error('Failed to reconnect SignalR:', error);
        setConnectionState({ 
          isConnected: false, 
          isConnecting: false,
          error: error instanceof Error ? error.message : 'Reconnection failed'
        });
        return false;
      }
    }

    return currentConnection.state === signalR.HubConnectionState.Connected;
  };

  const joinSessionGroup = async (sessionId: string): Promise<void> => {
    const isConnected = await ensureConnection();
    if (isConnected && connectionRef.current) {
      try {
        await connectionRef.current.invoke('JoinSessionGroupAsync', sessionId);
      } catch (error) {
        console.error('Failed to join session group:', error);
      }
    }
  };

  const leaveSessionGroup = async (sessionId: string): Promise<void> => {
    const isConnected = await ensureConnection();
    if (isConnected && connectionRef.current) {
      try {
        await connectionRef.current.invoke('LeaveSessionGroupAsync', sessionId);
      } catch (error) {
        console.warn('Failed to leave session group (non-critical):', error);
      }
    }
  };

  const joinUserGroup = async (userId: string): Promise<void> => {
    const isConnected = await ensureConnection();
    if (isConnected && connectionRef.current) {
      try {
        await connectionRef.current.invoke('JoinUserGroupAsync', userId);
      } catch (error) {
        console.warn('Failed to join user group (non-critical):', error);
      }
    }
  };

  const leaveUserGroup = async (userId: string): Promise<void> => {
    const isConnected = await ensureConnection();
    if (isConnected && connectionRef.current) {
      try {
        await connectionRef.current.invoke('LeaveUserGroupAsync', userId);
      } catch (error) {
        console.warn('Failed to leave user group (non-critical):', error);
      }
    }
  };

  const joinExecutionGroup = async (executionId: string): Promise<void> => {
    const isConnected = await ensureConnection();
    if (isConnected && connectionRef.current) {
      try {
        await connectionRef.current.invoke('JoinExecutionGroupAsync', executionId);
      } catch (error) {
        console.error('Failed to join execution group:', error);
      }
    }
  };

  const leaveExecutionGroup = async (executionId: string): Promise<void> => {
    const isConnected = await ensureConnection();
    if (isConnected && connectionRef.current) {
      try {
        await connectionRef.current.invoke('LeaveExecutionGroupAsync', executionId);
      } catch (error) {
        console.warn('Failed to leave execution group (non-critical):', error);
      }
    }
  };

  const onExecutionEvent = (callback: (event: ExecutionEvent) => void): void => {
    const currentConnection = connectionRef.current;
    if (!currentConnection) {
      console.warn('No SignalR connection available for onExecutionEvent');
      return;
    }
    
    const eventTypes = [
      'ExecutionStarted',
      'OrchestratorDecision',
      'SearchParametersGenerated',
      'SearchCompleted',
      'OpinionsDropped',
      'AgentStepStarted',
      'AgentStepCompleted',
      'ExecutionCompleted',
      'ClarificationRequested',
      'ClarificationReceived',
      'FolioExpansionCompleted'
    ];

    eventTypes.forEach(eventType => {
      currentConnection.off(eventType);
    });
    
    eventTypes.forEach(eventType => {
      currentConnection.on(eventType, (event: ExecutionEvent) => {
        callback(event);
      });
    });
  };

  const onStatusUpdate = (callback: (message: string) => void): void => {
    const currentConnection = connectionRef.current;
    if (!currentConnection) return;
    
    currentConnection.off('ResearchStatus');
    currentConnection.on('ResearchStatus', callback);
  };

  const subscribeToExecution = (executionId: string, handlers: Record<string, (event: any) => void>): void => {
    const currentConnection = connectionRef.current;
    if (!currentConnection) {
      console.warn('No SignalR connection available for subscribeToExecution');
      return;
    }

    executionHandlersRef.current.set(executionId, handlers);
    joinExecutionGroup(executionId);

    const eventTypes = Object.keys(handlers);
    eventTypes.forEach(eventType => {
      const handler = (event: any) => {
        if (event.executionId === executionId) {
          handlers[eventType]?.(event);
        }
      };

      const wrappedHandlers = executionHandlersRef.current.get(executionId);
      if (wrappedHandlers) {
        wrappedHandlers[`_wrapped_${eventType}`] = handler;
      }

      currentConnection.on(eventType, handler);
    });
  };

  const unsubscribeFromExecution = (executionId: string): void => {
    const currentConnection = connectionRef.current;
    if (!currentConnection) return;

    const handlers = executionHandlersRef.current.get(executionId);
    if (handlers) {
      Object.keys(handlers).forEach(key => {
        if (key.startsWith('_wrapped_')) {
          const eventType = key.replace('_wrapped_', '');
          currentConnection.off(eventType, handlers[key]);
        }
      });

      executionHandlersRef.current.delete(executionId);
    }

    leaveExecutionGroup(executionId);
  };

  const submitClarification = async (executionId: string, userResponse: string): Promise<void> => {
    const isConnected = await ensureConnection();
    if (isConnected && connectionRef.current) {
      try {
        await connectionRef.current.invoke('SubmitClarificationResponseAsync', executionId, userResponse);
      } catch (error) {
        console.error('Failed to submit clarification response:', error);
        throw error;
      }
    }
  };

  return (
    <SignalRContext.Provider value={{
      connectionState,
      joinUserGroup,
      leaveUserGroup,
      joinSessionGroup,
      leaveSessionGroup,
      joinExecutionGroup,
      leaveExecutionGroup,
      onExecutionEvent,
      onStatusUpdate,
      subscribeToExecution,
      unsubscribeFromExecution,
      submitClarification
    }}>
      {children}
    </SignalRContext.Provider>
  );
};
